import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { showErrorToast } from '@/components/toast/globalToast';
import { User, ClientProfile, AuthContextType } from '@/types/auth';
import { setBearerTokenFromLocalStorage } from '@/utils/axios';
import { toast } from 'react-toastify';
import { storage, getUserData } from '@/utils/storage';
import { isValidEmail } from '@/utils/emailValidator';
import {
  signIn as clientSignIn,
  getClientMe,
  authProvider,
} from '@/services/api/clientAuth';

// Single source of truth for a `User`: sensible defaults + caller overrides.
const createUser = (overrides: Partial<User>): User => {
  const now = new Date().toISOString();
  return {
    uid: '',
    username: '',
    email: null,
    displayName: '',
    firstName: '',
    lastName: '',
    photoURL: null,
    role: 'admin',
    isActive: true,
    lastLogin: now,
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
};

// Minimal profile used to sign the user in the instant we have a valid
// token — so loading the client profile can never block/undo sign-in.
// (`username` must be truthy for the session-restore check.)
const makeFallbackUser = (email: string): User =>
  createUser({
    uid: email,
    username: email,
    email,
    displayName: email,
    firstName: email,
  });

// Best-effort email claim from the JWT, for restoring a session with no cache.
const emailFromToken = (jwt: string): string | null => {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return claims.email || claims.identifier || claims.username || null;
  } catch {
    return null;
  }
};

// Map the client (company) entity onto the local `User` shape the UI reads.
// `emailHint` carries the login email (the /clients/me payload has none);
// on refresh we preserve whatever email was already stored.
const clientToUser = (
  client: ClientProfile,
  emailHint?: string | null
): User => {
  const existing = getUserData();
  return createUser({
    uid: client.uuid || String(client.id),
    // `username` must be truthy — the session-restore check requires it.
    username: client.code || client.name || existing?.username || 'client',
    email: emailHint ?? existing?.email ?? null,
    displayName: client.name,
    firstName: client.name,
    photoURL: client.logoImgUrl,
    role: existing?.role || 'admin',
    isActive: client.isActive,
    createdAt: client.createdAt,
    updatedAt: client.updatedAt,
  });
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  // isInitializing → first-load session restore (drives the full-page loader).
  // isLoading      → an in-flight action like sign-in (drives the button only).
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user && !!token;

  // Fetch GET /clients/me, then sync state + localStorage. Throws on failure
  // so callers can decide how to react (a 401 also triggers the axios
  // interceptor's force-sign-out).
  const loadClientProfile = useCallback(
    async (emailHint?: string | null): Promise<ClientProfile> => {
      const data = await getClientMe();
      const profile = clientToUser(data, emailHint);
      storage.setItem('client', JSON.stringify(data));
      storage.setItem('profile', JSON.stringify(profile));
      setClient(data);
      setUser(profile);
      return data;
    },
    []
  );

  // Restore the session on first load. If a token is present we ALWAYS
  // re-fetch GET /clients/me so the profile is fresh. Cached values paint
  // instantly to avoid a loader flash while the refresh happens.
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedToken = storage.getItem('token');
        if (!storedToken) {
          setUser(null);
          setToken(null);
          setClient(null);
          return;
        }

        setBearerTokenFromLocalStorage();
        setToken(storedToken);

        // Optimistic paint from cache (if we have it).
        const cachedProfile = getUserData();
        const cachedClientRaw = storage.getItem('client');
        const hasCache = !!(cachedProfile && cachedProfile.username);

        if (hasCache) {
          setUser(cachedProfile);
          if (cachedClientRaw) {
            try {
              setClient(JSON.parse(cachedClientRaw) as ClientProfile);
            } catch {
              /* ignore malformed cache */
            }
          }
          // Refresh in the background; a 401 trips the axios interceptor.
          loadClientProfile(cachedProfile.email).catch((err) =>
            console.error('Failed to refresh client profile:', err)
          );
        } else {
          // No cached profile, but the token itself means we're signed in.
          // Try to load the real profile; if it fails for anything other than
          // auth (the 401 interceptor handles sign-out), keep the session with
          // a minimal profile instead of bouncing back to /signin.
          const hintedEmail = emailFromToken(storedToken);
          try {
            await loadClientProfile(hintedEmail);
          } catch (err: any) {
            if (err?.response?.status === 401) throw err; // let sign-out stand
            console.error('Failed to load client profile during restore:', err);
            const provisional = makeFallbackUser(hintedEmail || 'client');
            storage.setItem('profile', JSON.stringify(provisional));
            setUser(provisional);
          }
        }
      } catch (err) {
        console.error('Critical error in auth check:', err);
        storage.clear();
        setUser(null);
        setToken(null);
        setClient(null);
      } finally {
        setIsInitializing(false);
      }
    };

    restoreSession();
  }, [loadClientProfile]);

  // ─── Email/password sign-in via client-user auth API ───────────────
  // Mirrors next-portal-v2-web: POST /client-user/v1/auth/login with
  // { identifier, credential, provider }, then stores the accessToken.
  const signIn = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const inputEmail = email.trim();

      if (!inputEmail || !password) {
        throw new Error('Masukkan email dan kata sandi Anda.');
      }
      if (!isValidEmail(inputEmail)) {
        throw new Error('Masukkan alamat email yang valid.');
      }

      const res = await clientSignIn({
        identifier: inputEmail,
        credential: password,
        provider: authProvider.EMAIL,
      });

      const accessToken = res?.data?.accessToken;
      if (!accessToken) {
        throw new Error('Berhasil masuk, tetapi token akses tidak ditemukan.');
      }

      // A valid token means the user is signed in. Persist it and set a
      // provisional profile immediately, so loading /clients/me can NEVER
      // block or undo sign-in.
      storage.setItem('token', accessToken);
      const provisional = makeFallbackUser(inputEmail);
      storage.setItem('profile', JSON.stringify(provisional));
      setBearerTokenFromLocalStorage();
      setUser(provisional);
      setToken(accessToken);

      // Enrich with the real client profile in the background (non-blocking).
      void loadClientProfile(inputEmail).catch((profileErr) =>
        console.error('Failed to load client profile after sign-in:', profileErr)
      );
    } catch (err: any) {
      // Backend error envelope: { status, message, errors }.
      // `errors` is polymorphic — verified against staging:
      //   - auth failure → [{ message: "Email atau kata sandi anda salah" }]
      //   - validation   → "Client timezone is required" (plain string)
      const errorData = err?.response?.data;
      const errors = errorData?.errors;

      let backendMessage = '';
      if (Array.isArray(errors)) {
        backendMessage = errors
          .map((e: any) => (typeof e === 'string' ? e : e?.message))
          .filter(Boolean)
          .join(' ');
      } else if (typeof errors === 'string') {
        backendMessage = errors;
      }

      // Prefer the specific `errors` detail; fall back to top-level message,
      // then a thrown client-side validation error, then a generic string.
      const message =
        backendMessage ||
        errorData?.message ||
        (err instanceof Error ? err.message : '') ||
        'Gagal masuk. Silakan coba lagi.';

      setError(message);
      showErrorToast('Gagal masuk', message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      storage.clear();
      setUser(null);
      setToken(null);
      setClient(null);

      if (window.axios) {
        delete window.axios.defaults.headers.common['Authorization'];
      }

      toast.success('Berhasil keluar!');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to sign out';
      setError(errorMessage);
      showErrorToast('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  // Re-fetch GET /clients/me on demand (e.g. after a profile update).
  const refreshClient = useCallback(async (): Promise<void> => {
    try {
      await loadClientProfile();
    } catch (err) {
      console.error('Failed to refresh client profile:', err);
    }
  }, [loadClientProfile]);

  const value: AuthContextType = {
    user,
    client,
    token,
    isLoading,
    isInitializing,
    error,
    isAuthenticated,
    signIn,
    signOut,
    refreshClient,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

declare global {
  interface Window {
    axios?: any;
  }
}
